import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Optional,
    Output,
    QueryList,
    Renderer2,
    Self,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatColumnDef, MatHeaderRow, MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { MatCardHeader, MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BehaviorSubject, combineLatest, EMPTY, filter, merge, Observable, startWith, of as observableOf, from, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, map, switchMap, tap } from 'rxjs/operators';
import { ButtonGroupDirective } from '../directives/button.directive';
import { TableCheckboxColumnDefDirective, TableCheckboxRowSelection } from './table-checkbox-column.directive';
import { AppTableActionsDirective, AppTableDirective, AppTableRowDirective, AppTableExpandedRowDirective } from './table.directive';
import { TableColumn, TableTypeNames } from 'app/services/column.service';
import { TableColumnPreferenceService } from 'app/services/table-column-preference.service';
import { HostListenerService } from 'app/services/host-listener.service';
import { PaginationMeta } from 'app/models/entities/pagination';
import { TableFilters, TableFiltersSelection } from 'app/models/entities/table-filters';
import { ColumnMap } from 'app/models/entities/column-map';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { AnimatedDelayDirective, AnimatedDirective } from '../directives/animated.directive';
import { EmptyStateModule } from '../empty-state/empty-state.module';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { AppLoadingTableStateComponent } from './loading-table-state/loading-table-state.component';
import { FilteringService } from '../../../../services/filtering.service';
import { FilterSortPersistenceService } from '../../../../services/filter-sort-persistence.service';

export interface SelectedEvent<T>
{
    loading$: BehaviorSubject<boolean>;
    source: T;
}

export interface LoadedEvent<T>
{
    data: T[];
    mode: string;
    initial: boolean;
    initialEmptyState: boolean;
    loading: boolean;
}

export enum TableModes {
    DATA_ARRAY,
    DATA_SOURCE,
}

export enum TableChangeSource {
    LOAD = 'Load',
    PARAMS = 'Query Params',
    REFRESH = 'Refresh',
    SORT = 'Sort',
    PAGE = 'Page',
    SEARCH = 'Search',
    FILTER = 'Filter',
}

/**
 * Dual-mode data table supporting both client-side (DATA_ARRAY) and server-side (DATA_SOURCE) operations.
 *
 * Mode selection determines data flow architecture - DATA_ARRAY mode handles all sorting/filtering/pagination
 * in-memory after initial fetch, while DATA_SOURCE mode triggers API calls for each table state change.
 * Column persistence via ColumnService allows users to maintain custom column visibility across sessions.
 * Row selection events include loading states to enable optimistic UI updates during async operations.
 */
@UntilDestroy()
@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: true,
    imports: [
        MatCardModule,
        MatTableModule,
        MatSortModule,
        AsyncPipe,
        MatProgressSpinnerModule,
        MatIconModule,
        MatProgressBarModule,
        MatPaginatorModule,
        NgIf,
        MatDividerModule,
        NgForOf,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatToolbarModule,
        MatMenuModule,
        FlexSpacerDirective,
        AppTableRowDirective,
        AppTableDirective,
        AnimatedDirective,
        AnimatedDelayDirective,
        EmptyStateModule,
        AppLoadingTableStateComponent,
        ButtonGroupDirective,
        NgTemplateOutlet,
        TableCheckboxColumnDefDirective,
    ],
})
export class AppTableComponent<T> implements OnChanges, OnInit, AfterContentInit, AfterViewInit
{
    @Input() data: T[]|MatTableDataSource<T> = [];
    @Input() type: TableTypeNames;
    @Input() columnMap: ColumnMap[] = [];
    @Input() rowSelectable = false;
    @Input() hasFilters = true;
    @Input() searchPlaceholder = 'Search....';
    @Input() searchControl = new FormControl<string>({ value: '', disabled: true });
    @Input() pageSizeOptions = [
        5,
        10,
        25,
        50,
        100,
    ];

    /**
     * Default page size for pagination. Must be a value in pageSizeOptions.
     * If not specified, defaults to pageSizeOptions[1] (10) for 'default' style
     * or pageSizeOptions[0] (5) for 'v2' style.
     */
    @Input() defaultPageSize: number;

    /**
     * Paginator style: 'default' uses Angular Material mat-paginator,
     * 'v2' uses custom styled paginator matching Figma design.
     */
    @Input() paginatorStyle: 'default' | 'v2' = 'default';

    /**
     * When true, shows skeleton loader during pagination (page changes).
     * Useful for server-side paginated tables where loading new pages takes time.
     */
    @Input() showSkeletonOnPagination = false;

    // support checkbox column selections
    @Input() hasCheckboxSelection = false;
    /** Rows this predicate answers true for get a disabled checkbox and are skipped by select-all. */
    @Input() checkboxRowDisabledFn: (row: T) => boolean;
    @Input() showSelectionCount = true;
    @Input() selection: TableCheckboxRowSelection;

    /**
     * Function to determine if a row should display warning styling (yellow background).
     * Return true to apply the warning-row class to that row.
     *
     * PERFORMANCE NOTE: This function is called on every change detection cycle for each row.
     * Keep the logic simple (e.g., property checks) to avoid performance issues with large
     * datasets (200+ rows). For complex logic, pre-compute the warning state when data loads.
     */
    @Input() rowWarningFn: (row: T) => boolean;

    /**
     * Function to determine if a row should display disabled styling (muted text, no hover/arrow).
     * Return true to apply the disabled-row class to that row.
     * Disabled rows cannot be selected.
     */
    @Input() rowDisabledFn: (row: T) => boolean;

    /**
     * SD-9537: return true for the ONE row that starts a new group, and it gets a grey band above
     * it — the same grey as the column header's background, so a section break inside the table
     * reads the way the header does.
     *
     * Only the group's FIRST row, not every member: a caller that marks them all gets a band above
     * each one. Deciding which row that is needs the rendered order, which this table owns — so a
     * caller pairs this with a `sortData` that keeps the group contiguous and reads the boundary off
     * that (see the assigned-sales-reps table for the pattern).
     *
     * PERFORMANCE NOTE: called on every change detection cycle for each row, exactly like
     * the `rowWarningFn` input. Keep it to a property read.
     */
    @Input() rowGroupDividerFn: (row: T) => boolean;

    /**
     * SD-9501: return true for a summary row — one that renders in the table but is not a result,
     * e.g. a report's grand-total row. Summary rows are kept out of sorting, paging and the
     * paginator's "of N results" count, and render after the rows of every page, in their
     * original order. `resultsLength` (which drives the empty states) still counts them.
     *
     * DATA_ARRAY mode only — server-paginated tables page and count on the server. Read when the
     * data source is attached, so bind it statically rather than swapping it after load. Only for
     * rows that belong at the end: a subtotal that closes its own group would be moved away from it.
     */
    @Input() summaryRowFn: (row: T) => boolean;

    /**
     * When true, rows with a deletedAt property will display with deleted styling (opacity, strikethrough).
     * Set to false to suppress this styling, e.g., for archived tabs where all rows are deleted.
     */
    @Input() showDeletedRowStyle = true;

    /**
     * When true, applies compact row styling with reduced cell padding (0.5rem vs 15px default).
     */
    @Input() compact = false;

    /**
     * When true, multiple rows can be expanded simultaneously.
     * When false, expanding a row collapses any previously expanded row (accordion behavior).
     */
    @Input() expandMultiple = true;

    /**
     * Property name to use as a stable identity for expanded row tracking.
     * When set, expanded rows persist through data refreshes by matching
     * the property value rather than using object reference equality.
     */
    @Input() expandTrackBy: string;

    /**
     * Query param keys to exclude from the filter count.
     * Useful when the page uses query params for non-filter purposes (e.g., tab state).
     */
    @Input() excludeFromFilterCount: string[] = [];

    /**
     * SD-7319 — opt-in: persist filter, search, and sort state to localStorage
     * keyed by [type], so the table restores its last-applied state on init.
     *
     * Requires [type] to be set so a stable storage key can be derived. When
     * disabled (default), the component behaves exactly as before — no reads,
     * no writes, no subscriptions added.
     *
     * Storage shape mirrors the eventual backend keys established by SD-6481
     * (`filters.<tableId>`, `sort.<tableId>`) so the Phase-2 migration off
     * localStorage is a 1-1 swap. See FilterSortPersistenceService.
     */
    @Input() persistFilterSort = false;

    /**
     * MatSort input for tables that define @ViewChild(MatSort, { static: true }) in the parent.
     *
     * This input-based approach works when:
     * - The table is NOT inside an *ngIf or other structural directive
     * - The parent can use { static: true } to resolve MatSort before ngOnInit
     *
     * Example: pmc-list.component.ts uses this pattern successfully.
     *
     * For tables inside *ngIf (like bank-reconciliation-landing), { static: true } cannot be used
     * because the DOM element doesn't exist at component initialization. Those tables should
     * place the matSort directive directly on <app-table> and rely on the host injection
     * via effectiveSort getter below.
     *
     * Both approaches are fully supported and backwards compatible.
     */
    @Input() columnSort: MatSort;
    @Input() sortingDataAccessor: (data: T, sortHeaderId: string) => any;

    /**
     * Full control of the row ORDER, for the rare table that needs a sort key the user's chosen
     * column cannot express (SD-9537).
     *
     * `sortingDataAccessor` can only map one row + one column to one comparable value, and
     * MatTableDataSource then flips the whole comparison for a descending sort — so it cannot
     * express "always order by X first, then by whatever column the user clicked". A table that
     * needs that supplies this instead.
     *
     * OPT-IN and falsy by default: when it is not set, MatTableDataSource keeps its own `sortData`,
     * so every existing table behaves exactly as before. Same extension shape as
     * the `sortingDataAccessor` and `filterPredicate` inputs.
     */
    @Input() sortData: (data: T[], sort: MatSort) => T[];

    /**
     * Returns the effective MatSort instance - either the passed input or the host-injected one.
     *
     * This getter solves the ExpressionChangedAfterItHasBeenCheckedError that occurs when:
     * - A table is inside an *ngIf (e.g., bank-reconciliation-landing)
     * - The parent cannot use @ViewChild(MatSort, { static: true }) because the element
     *   doesn't exist at initialization time
     *
     * Solution: Place matSort directive directly on <app-table> element. The MatSort is then
     * injected via @Optional() @Self() in the constructor and accessed through this getter.
     *
     * Priority: columnSort input (for backwards compatibility) > host-injected _hostSort
     */
    get effectiveSort(): MatSort | undefined {
        return this.columnSort || this._hostSort;
    }
    @Input() filterPredicate: (data: T, filterString: string) => boolean;

    @Output() search = new EventEmitter<string>();
    @Output() showFilters = new EventEmitter<TableFilters>();
    @Output() rowSelect = new EventEmitter<SelectedEvent<T>>();
    @Output() loaded = new EventEmitter<LoadedEvent<T>>();
    @Output() checkboxSelection = new EventEmitter<TableCheckboxRowSelection>();

    @ViewChild(MatTable, {static: true}) table: MatTable<T>;
    @ViewChild(MatTable, {read: ElementRef}) tableRef: ElementRef;
    @ViewChild(MatHeaderRow, {read: ElementRef}) tableHeaderRef: ElementRef;
    @ViewChild(MatMenuTrigger) columnMenuTrigger: MatMenuTrigger;

    /**
     * Paginator ViewChild with setter to handle *ngIf timing.
     * The mat-paginator is inside *ngIf="paginatorStyle === 'default'", so it's not
     * available during ngAfterViewInit. This setter sets up the bridge to pageChange$
     * when the paginator becomes available.
     */
    private _paginator: MatPaginator;
    private _paginatorBridgeSubscription: Subscription;

    @ViewChild(MatPaginator)
    set paginator(p: MatPaginator) {
        this._paginator = p;

        // Clean up previous subscription if paginator changes
        if (this._paginatorBridgeSubscription) {
            this._paginatorBridgeSubscription.unsubscribe();
            this._paginatorBridgeSubscription = null;
        }

        // Set up bridge when paginator becomes available (for default paginatorStyle)
        if (p && this.paginatorStyle === 'default') {
            this._paginatorBridgeSubscription = p.page.pipe(untilDestroyed(this)).subscribe(() => {
                this.pageChange$.next();
            });
        }
    }

    get paginator(): MatPaginator {
        return this._paginator;
    }
    @ViewChild(TableCheckboxColumnDefDirective) checkboxDirective: TableCheckboxColumnDefDirective<T>;

    @ContentChild(EmptyStateComponent) emptyStateRef!: EmptyStateComponent;
    @ContentChild(MatCardHeader) cardHeader!: MatCardHeader;
    @ContentChild(MatFormField) formFieldRef!: MatFormField;
    @ContentChild(AppTableActionsDirective) tableActions!: AppTableActionsDirective;
    @ContentChild(AppTableExpandedRowDirective) expandedRowTemplate!: AppTableExpandedRowDirective<T>;
    @ContentChildren(MatColumnDef) columnDefs!: QueryList<MatColumnDef>;

    selectedColumns = new SelectionModel<TableColumn>(true, []);
    resultsLength: number = 0;
    /** `resultsLength` minus rows matched by `summaryRowFn`; what the paginators count. */
    countedResultsLength: number = 0;
    /** Data sources whose paging `_keepSummaryRowsOutOfPaging` already patched; it runs on every attach. */
    private _summaryRowSources = new WeakSet<MatTableDataSource<T>>();
    /** sortData wrappers made by `_keepSummaryRowsOutOfPaging`, so a re-attach doesn't wrap one twice. */
    private _summaryRowSorts = new WeakSet<MatTableDataSource<T>['sortData']>();
    isLoadingInitialResults = true;
    isLoadingResults = true;
    initialEmptyState = false;
    rowsAnimated = true;
    expandedRows = new SelectionModel<T>(true);

    readonly loadingRow$: { [prop: string]: BehaviorSubject<boolean>; } = {};
    readonly loadingState$ = new BehaviorSubject<boolean>(true);
    readonly data$ = new BehaviorSubject<T[] | { data: T[]; meta: PaginationMeta }>(null);

    private mode: TableModes = TableModes.DATA_SOURCE;
    private initialResultsLength = -1;
    private _requestedSearch = '';

    private readonly src = TableChangeSource;
    private readonly tableStateChange$ = new BehaviorSubject<TableChangeSource>(this.src.LOAD);

    private readonly filtersState$ = new BehaviorSubject<TableFiltersSelection>(new TableFiltersSelection({}));
    private readonly searchState$ = new BehaviorSubject<string>('');
    private readonly refresh$ = new BehaviorSubject<void>(null);
    private readonly forceRefresh$ = new Subject<void>();
    private readonly pageChange$ = new Subject<void>();
    private readonly columnChange$ = new BehaviorSubject<void>(null);

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _filteringService: FilteringService,
        private _host: HostListenerService,
        private _renderer: Renderer2,
        private _cd: ChangeDetectorRef,
        private _router: Router,
        private _filterSortPersistence: FilterSortPersistenceService,
        private _columnPrefs: TableColumnPreferenceService,
        /**
         * Host-injected MatSort for tables that place the matSort directive directly on <app-table>.
         * Used when the table is inside an *ngIf and cannot use @ViewChild with { static: true }.
         * See effectiveSort getter and columnSort input comments for full explanation.
         */
        @Optional() @Self() private _hostSort: MatSort,
    ) {
    }

    /**
     * Distinguishes between initial empty state (no data exists) vs filtered empty state.
     *
     * Initial empty state typically shows onboarding content while filtered empty state
     * indicates no matching results for current search/filter criteria.
     */
    @HostBinding('class.empty-table-onload') get addInitialEmptyStateClass() {
        return this.initialEmptyState;
    }

    @HostBinding('class.empty-table-onchange') get addChangeEmptyStateClass() {
        // Counted results, not resultsLength: with `summaryRowFn` a page of only summary rows
        // renders empty, and this class is what releases the table's min-width for the empty state.
        // Without `summaryRowFn` the two counts are always equal.
        return !this.initialEmptyState && this.countedResultsLength === 0;
    }

    /**
     * Tables with only 2 columns should not have row borders (per design).
     * This counts visible non-utility columns (excludes 'select', 'arrowRight', 'mobile').
     */
    @HostBinding('class.compact') get isCompact() {
        return this.compact;
    }

    @HostBinding('class.no-row-borders') get isTwoColumnTable() {
        const utilityColumns = ['select', 'arrowRight', 'mobile'];
        const visibleDataColumns = this.displayedColumns.filter(
            (column) => !utilityColumns.includes(column.key) && !column.isMobile
        );
        return visibleDataColumns.length <= 2;
    }

    get hasData(): boolean {
        return !this.initialEmptyState && this.resultsLength > 0;
    }

    get dataAsDataSource() {
        return this.data as MatTableDataSource<T>;
    }

    get displayedColumns(): ColumnMap[] {
        return this.columnMap.filter((column) => {
            return this.selectedColumns.isSelected(column);
        });
    }

    get displayedColumnKeys$() {
        // Combine mobile mode with column changes to ensure re-emission when columns change
        return combineLatest([
            this._host.isMobileMode(),
            this.columnChange$,
        ]).pipe(map(([isMobile]) => {
            let columns: string[];
            if (isMobile) {
                columns = this.displayedColumns.filter((column) => !!column.isMobile).map((column) => column.key);
            } else {
                columns = this.displayedColumns.filter((column) => !column.isMobile).map((column) => column.key);
            }

            // Add 'select' column at the beginning if checkbox selection is enabled
            if (this.hasCheckboxSelection && this.columnMap.some((column) => column.key === 'select') && !columns.includes('select')) {
                columns.unshift('select');
            }

            if (this.rowSelectable && columns.indexOf('actions') === -1 && !isMobile) {
                columns.push('arrowRight');
            }

            return columns;
        }));
    }

    get mobileMode$() {
        return this._host.isMobileMode();
    }

    get tabletMode$() {
        return this._host.isTabletMode();
    }

    get desktopMode$() {
        return this._host.isDesktopMode();
    }

    /**
     * Returns the effective default page size for the paginator.
     * Uses defaultPageSize input if provided, otherwise falls back to
     * pageSizeOptions[1] for 'default' style or pageSizeOptions[0] for 'v2' style.
     */
    get effectiveDefaultPageSize(): number {
        if (this.defaultPageSize != null) {
            return this.defaultPageSize;
        }
        return this.paginatorStyle === 'v2' ? this.pageSizeOptions[0] : this.pageSizeOptions[1];
    }

    /**
     * V2 Paginator helpers - returns the start index of current page (1-based)
     */
    get v2PaginatorStart(): number {
        if (!this.paginator || this.countedResultsLength === 0) { return 0; }
        return (this.paginator.pageIndex * this.paginator.pageSize) + 1;
    }

    /**
     * V2 Paginator helpers - returns the end index of current page
     */
    get v2PaginatorEnd(): number {
        if (!this.paginator || this.countedResultsLength === 0) { return 0; }
        const end = (this.paginator.pageIndex + 1) * this.paginator.pageSize;
        return Math.min(end, this.countedResultsLength);
    }

    /**
     * Result count for the paginators: the rows minus any matched by `summaryRowFn`.
     */
    private countResults(rows: T[]): number {
        if (!this.summaryRowFn) { return rows.length; }
        return rows.filter(row => !this.summaryRowFn(row)).length;
    }

    /**
     * V2 Paginator - check if previous page is available
     */
    get v2HasPreviousPage(): boolean {
        return this.paginator?.hasPreviousPage() ?? false;
    }

    /**
     * V2 Paginator - check if next page is available
     */
    get v2HasNextPage(): boolean {
        return this.paginator?.hasNextPage() ?? false;
    }

    /**
     * V2 Paginator navigation and page size methods.
     *
     * These use MatPaginator's public API methods (previousPage, nextPage) and explicit
     * page event emission rather than direct property assignment. This is critical because:
     *
     * - DATA_ARRAY mode (client-side pagination): MatTableDataSource subscribes to
     *   paginator.page events to re-slice displayed rows. Direct property assignment
     *   (e.g. paginator.pageIndex = x) does NOT emit page events, so the table never
     *   updates even though the paginator UI reflects the change.
     *   Examples: vendor-balance-summary-v2, aging-of-accounts-v2, custom-report-v2
     *
     * - DATA_SOURCE mode (server-side pagination): Uses pageChange$ observable to
     *   trigger API refetch. The extra page event emission is harmless here since
     *   DATA_SOURCE mode doesn't use MatTableDataSource.
     *   Examples: pmc-list, vendor-overview-v2
     */
    v2PreviousPage(): void {
        if (this.paginator?.hasPreviousPage()) {
            this.paginator.previousPage();
            this.pageChange$.next();
        }
    }

    v2NextPage(): void {
        if (this.paginator?.hasNextPage()) {
            this.paginator.nextPage();
            this.pageChange$.next();
        }
    }

    v2ChangePageSize(size: number): void {
        if (this.paginator) {
            const previousPageIndex = this.paginator.pageIndex;
            this.paginator.pageSize = size;
            this.paginator.pageIndex = 0;
            this.paginator.page.emit({
                pageIndex: 0,
                pageSize: size,
                previousPageIndex,
                length: this.paginator.length,
            });
            this.pageChange$.next();
        }
    }

    get tableState$() {
        return this.tableStateChange$.asObservable().pipe(
            debounceTime(150),
            map((src) => {
                return { data: this.data, event: src };
            }),
        );
    }

    get connectTable$() {
        this.mode = TableModes.DATA_ARRAY;
        return this._connect$;
    }

    get connectDataTable$() {
        this.mode = TableModes.DATA_SOURCE;
        return this._connect$;
    }

    get availableColumns() {
        return this.columnMap.filter((column) => {
            if (!column.title) {
                return false;
            }

            if (column.disabled) {
                return !column.disabled();
            }

            return true;
        });
    }

    get searchValue() {
        return this.searchState$.getValue();
    }

    private get _hasActiveSearch(): boolean {
        return !!this.searchValue?.trim();
    }

    get filters() {
        return this.filtersState$.getValue().filters;
    }

    /**
     * Returns the count of active filters (excluding 'all' values and excluded keys).
     * Used to display filter count badge next to the Filters button.
     */
    get filterCount(): number {
        const filters = this.filters;
        if (!filters) {
            return 0;
        }
        return Object.entries(filters).filter(([key, value]) => {
            // Exclude specified keys, 'all' values, and empty/null values
            if (this.excludeFromFilterCount.includes(key)) {
                return false;
            }
            // An empty tag list or a { value: 'all' } option object is a filter left at its default, not an active one.
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (value !== null && typeof value === 'object' && 'value' in (value as object)) {
                return (value as { value: unknown }).value !== 'all';
            }
            return value != null && value !== '' && value !== 'all';
        }).length;
    }

    set filters(filters: TableFilters) {
        this.filtersState$.next(new TableFiltersSelection(filters ?? {}));
    }

    set loading(isLoading: boolean) {
        const loaded: LoadedEvent<T> = {
            data: Array.isArray(this.data) ? this.data : this.dataAsDataSource.data,
            mode: TableModes[this.mode],
            initial: this.isLoadingInitialResults && !isLoading,
            initialEmptyState: !isLoading && this.initialResultsLength === 0 && this.resultsLength === 0,
            loading: !this.isLoadingResults,
        };

        if (loaded.initial) {
            this.isLoadingInitialResults = false;
        }

        // Enable search when data is available (not just on initial load)
        // This handles cases where initial load returns empty but subsequent refresh brings data.
        // SD-9496 — also enable it when a search term is active (e.g. one restored by
        // [persistFilterSort]) even if it matched nothing, or the user can never correct it.
        if (!isLoading && this.searchControl.disabled && (loaded.data.length > 0 || this._hasActiveSearch)) {
            this.searchControl.enable({ emitEvent: false });
        }

        // Always re-attach sort/paginator in DATA_ARRAY mode when loading completes
        // This handles cases where data source was replaced (e.g., tab switching)
        if (!isLoading && this.mode === TableModes.DATA_ARRAY && this.data instanceof MatTableDataSource) {
            this._attachMatTableDataSourceHandling(this.dataAsDataSource);
        }

        this.initialEmptyState = loaded.initialEmptyState;

        if (!isLoading && this.emptyStateRef) {
            this.emptyStateRef.searchTerm = this.searchControl.value?.trim() || '';
        }
        this.isLoadingResults = isLoading;
        this.loaded.emit(loaded);
        this._cd.markForCheck();
    }

    private get _connect$() {
        // by default, table only updates data for initial load and forced refresh
        let source$: Observable<any> = merge(this.refresh$.pipe(tap(() => this.tableStateChange$.next(this.src.REFRESH))));

        if (this.mode === TableModes.DATA_SOURCE) {
            // for DATA_SOURCE mode, listen to all changes to trigger fetch from api
            // Use EMPTY as fallback when effectiveSort is undefined (tables without sorting)
            const sortChange$ = this.effectiveSort?.sortChange.pipe(tap(() => this.tableStateChange$.next(this.src.SORT))) ?? EMPTY;
            // Listen to both paginator.page (for default style) and pageChange$ (for v2 style)
            const paginatorPage$ = this.paginator?.page.pipe(tap(() => this.tableStateChange$.next(this.src.PAGE))) ?? EMPTY;
            const pageChange$ = this.pageChange$.pipe(tap(() => this.tableStateChange$.next(this.src.PAGE)));
            source$ = merge(
                this._activatedRoute.queryParams.pipe(tap(() => this.tableStateChange$.next(this.src.PARAMS))),
                sortChange$,
                paginatorPage$,
                pageChange$,
                this.searchState$.pipe(tap(() => this.tableStateChange$.next(this.src.SEARCH))),
                this.filtersState$.pipe(tap(() => this.tableStateChange$.next(this.src.FILTER))),
                this.refresh$.pipe(tap(() => this.tableStateChange$.next(this.src.REFRESH))),
            );
        }

        // Normal source goes through filter (blocks during initial load)
        const filteredSource$ = source$.pipe(
            filter(() => !this.isLoadingInitialResults),
            map(() => {
                // Check if this is a pagination change and showSkeletonOnPagination is enabled
                const isPaginationChange = this.tableStateChange$.getValue() === this.src.PAGE;
                if (this.showSkeletonOnPagination && isPaginationChange) {
                    // Clear data and show skeleton for pagination
                    this.data = [];
                    this.isLoadingInitialResults = true;
                    this.rowsAnimated = true;
                } else if (!this.isLoadingInitialResults) {
                    this.rowsAnimated = false;
                }
                this.loading = true;
            }),
        );

        // Force refresh bypasses the filter for refreshAsInitialLoad()
        const forceRefreshSource$ = this.forceRefresh$.pipe(
            tap(() => this.tableStateChange$.next(this.src.REFRESH)),
            map(() => {
                this.rowsAnimated = true; // Enable animations for "initial" load
                this.loading = true;
            }),
        );

        return merge(filteredSource$, forceRefreshSource$).pipe(
            debounceTime(500),
            startWith({}),
            // SD-9496 — snapshot the search term the consumer is about to request with, so the
            // response is judged by what was ASKED for, not by whatever is typed when it arrives.
            tap(() => this._requestedSearch = this.searchValue),
        );
    }

    addFilter(filters: TableFilters) {
        this.filtersState$.next(this.filtersState$.getValue().add(filters));
    }

    removeFilter(key: string) {
        this.filtersState$.next(this.filtersState$.getValue().remove(key));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('columnMap')) {
            // Apply the user's stored selection (or system defaults). Reading
            // is synchronous from the preference cache, so columns render
            // instantly with no spinner.
            const keys = this.type ? this._columnPrefs.read(this.type) : null;
            this._applyStoredColumns(keys);
        }

        if (changes['expandMultiple'] && !changes['expandMultiple'].firstChange) {
            this.expandedRows = new SelectionModel<T>(this.expandMultiple);
        }
    }

    ngOnInit() {
        // Initialize SelectionModel with expandMultiple setting
        this.expandedRows = new SelectionModel<T>(this.expandMultiple);

        // Re-apply column selection when the backend value arrives (the
        // bulk GET on the authenticated shell resolves after first paint, so
        // a fresh device renders defaults instantly, then self-corrects to
        // the user's stored selection). Idempotent on values we wrote.
        if (this.type) {
            this._columnPrefs.watch(this.type).pipe(
                untilDestroyed(this),
            ).subscribe((keys) => {
                this._applyStoredColumns(keys);
                this._cd.markForCheck();
            });
        }

        // SD-7319 — when opt-in persistence is on, hydrate the URL with stored
        // filters BEFORE the queryParams subscription kicks off, so the existing
        // URL → filtersState$ flow does the work for us. The helper returns
        // `true` IFF it scheduled a merge navigation — in that case we MUST
        // skip the initial queryParams emission (the pre-merge URL) so the
        // table doesn't briefly load with the partial filter set. Otherwise
        // consumers like violations-list that fetch filter-aware widget-group
        // counts on every emission will visibly flicker from the empty-URL
        // result to the merged-URL result.
        const willHydrate = this._hydratePersistedFiltersIntoUrl();
        let skipPreHydrateEmit = willHydrate;

        this._activatedRoute.queryParams.pipe(
            filter((params) => !!params),
            filter(() => {
                if (skipPreHydrateEmit) {
                    skipPreHydrateEmit = false;

                    return false;
                }

                return true;
            }),
            untilDestroyed(this),
        ).subscribe((params) => {
            const filters: TableFilters = {};
            for(const key in params) {
                if (!!params[key] && (params[key] === 'true' || params[key] === 'false')) {
                    filters[key] = JSON.parse(params[key]);
                } else if (!!params[key] && (!isNaN(+params[key]) && params[key].trim() !== '')) {
                    filters[key] = +params[key];
                } else {
                    filters[key] = params[key];
                }
            }
            this.filters = filters;
        });

        // SD-7319 — persist filter + search state on every change (debounced).
        // Subscriptions only register when the consumer opts in AND provides a
        // stable storage key via [type].
        if (this.persistFilterSort && this.type) {
            this.filtersState$.pipe(
                debounceTime(300),
                untilDestroyed(this),
            ).subscribe((selection) => {
                this._filterSortPersistence.writeFilters(this.type, selection.filters);
            });

            this.searchState$.pipe(
                debounceTime(300),
                untilDestroyed(this),
            ).subscribe((value) => {
                this._filterSortPersistence.writeSearch(this.type, value ?? '');
            });

            // One-shot search hydration: the search control is disabled until
            // first data load completes; setting the value here is safe — the
            // FormControl re-enables itself in the loaded-event handler.
            const storedSearch = this._filterSortPersistence.readSearch(this.type);
            if (storedSearch) {
                this.searchControl.setValue(storedSearch, { emitEvent: false });
                this.searchState$.next(storedSearch);
            }
        }

        this.data$.asObservable().pipe(
            filter((res) => !!res),
            // SD-9496 — pair each response with the search it was requested with BEFORE the
            // delay, so a later keystroke/clear can't relabel it while it waits.
            map((res) => ({ res, searched: !!this._requestedSearch?.trim() })),
            delay(150),
            untilDestroyed(this),
        ).subscribe(({ res, searched }: { res: T[] | { data: T[], meta: PaginationMeta }, searched: boolean }) => {
            if (Array.isArray(res)) {
                this.data = this.mode === TableModes.DATA_ARRAY ? new MatTableDataSource<T>(res) : res;
                this.resultsLength = res.length;
                this.countedResultsLength = this.countResults(res);
            } else if (res.hasOwnProperty('data') && res.hasOwnProperty('meta')) {
                this.data = res.data;
                this.resultsLength = res.meta.total || res.data.length;
                this.countedResultsLength = this.resultsLength;
            } else {
                console.warn('Invalid Data source!');
                this.resultsLength = 0;
                this.countedResultsLength = 0;
                this.data = [];
            }

            // track totals for initial load vs. subsequent loads (e.g. after filter or sort)
            // SD-9496 — a server-side search (e.g. one restored by [persistFilterSort]) is not
            // an unfiltered baseline: latching its zero would flip the table to the onboarding
            // empty state and hide the toolbar, leaving the search stuck. Wait for an unsearched load.
            const isSearchedLoad = this.mode === TableModes.DATA_SOURCE && searched;
            if (this.initialResultsLength === -1 && !isSearchedLoad) {
                this.initialResultsLength = this.resultsLength;
            }

            // Re-sync expanded rows after data refresh when tracking by property
            if (this.expandTrackBy && this.expandedRows.selected.length) {
                const expandedIds = new Set(this.expandedRows.selected.map(r => r[this.expandTrackBy]));
                const dataArray = Array.isArray(res) ? res : res.data;
                this.expandedRows.clear();
                dataArray
                    .filter(row => expandedIds.has(row[this.expandTrackBy]))
                    .forEach(row => this.expandedRows.select(row));
            }

            // check against empty state for min-width
            this._setMinWidthForTable();

            this.loading = false;
        });
    }

    ngAfterContentInit() {
        this.columnDefs.forEach(columnDef => this.table.addColumnDef(columnDef));
        this._cd.detectChanges();
    }

    ngAfterViewInit() {
        // initial load of table, set min-width used
        this._setMinWidthForTable();

        // Seed the checkbox column from a caller-provided initial selection
        // (e.g. an edit page pre-selecting the rows already attached to the
        // entity). Without this, [selection] only feeds labels/events and
        // pre-selected ids never render as checked.
        if (this.hasCheckboxSelection && this.selection?.ids?.length && this.checkboxDirective?.instance) {
            const instance = this.checkboxDirective.instance;
            if (instance.selectedRows.isEmpty()) {
                instance.selectedRows.select(...this.selection.ids);
                instance.selectedCount = this.selection.ids.length;
            }
        }

        this.effectiveSort?.sortChange.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                if (this.mode !== TableModes.DATA_ARRAY) {
                    this.paginator.pageIndex = 0;
                }
            },
        });

        // SD-7319 — apply stored sort + subscribe to future changes. Runs here
        // (not in ngOnInit) because effectiveSort isn't resolved until the view
        // child / host-injected MatSort is ready.
        if (this.persistFilterSort && this.type && this.effectiveSort) {
            const storedSort = this._filterSortPersistence.readSort(this.type);
            if (storedSort && storedSort.active && storedSort.direction) {
                // Defer to the next microtask so the initial sort emission
                // doesn't fire mid-AfterViewInit (avoids ExpressionChanged...).
                //
                // Assigned directly rather than via MatSort.sort(): sort() only applies
                // `start` when the id DIFFERS from the active one, and otherwise advances
                // the direction cycle. So a stored sort matching the table's own
                // matSortActive replayed inverted — or, at the end of the cycle, as ''
                // which the subscriber below then persists as null, wiping the user's
                // saved sort. Assignment applies exactly what was stored either way.
                Promise.resolve().then(() => {
                    this.effectiveSort.active = storedSort.active;
                    this.effectiveSort.direction = storedSort.direction;
                    this.effectiveSort.sortChange.emit({
                        active: storedSort.active,
                        direction: storedSort.direction,
                    });
                });
            }

            this.effectiveSort.sortChange.pipe(
                debounceTime(300),
                untilDestroyed(this),
            ).subscribe((sort) => {
                if (!sort.active || (sort.direction !== 'asc' && sort.direction !== 'desc')) {
                    this._filterSortPersistence.writeSort(this.type, null);

                    return;
                }
                this._filterSortPersistence.writeSort(this.type, {
                    active: sort.active,
                    direction: sort.direction,
                });
            });
        }

        this.searchControl.valueChanges.pipe(
            debounceTime(500),
            switchMap((value) => {
                this.searchState$.next(value);
                if (this.mode === TableModes.DATA_ARRAY) {
                    this.dataAsDataSource.filter = value.trim().toLowerCase();
                    // Keep the displayed result count in sync with the client-side filtered rows
                    // (otherwise the paginator footer shows the unfiltered total on search).
                    this.resultsLength = this.dataAsDataSource.filteredData.length;
                    this.countedResultsLength = this.countResults(this.dataAsDataSource.filteredData);
                    return observableOf(value).pipe(delay(500));
                }
                return observableOf(value);
            }),
            untilDestroyed(this),
        ).subscribe({
            next: () => this.paginator.pageIndex = 0,
        });
    }

    /**
     * Emits row selection with loading state for optimistic UI updates.
     *
     * Loading state allows parent components to show immediate feedback while
     * async operations complete (e.g., navigation, API calls). Parent must call
     * loading$.next(false) when operation completes.
     */
    trackByValue(_index: number, value: number): number {
        return value;
    }

    selectRow(row: T) {
        if (this.rowSelectable && !(this.rowDisabledFn?.(row))) {
            this.loadingRow$[row['id']] = new BehaviorSubject<boolean>(true);

            this.rowSelect.emit({
                loading$: this.loadingRow$[row['id']], source: row,
            });
        }
    }

    /**
     * Reloads data without resetting the initial empty state tracking.
     * This preserves the toolbar/search when a filter or mutation returns zero results.
     *
     * IMPORTANT: Do NOT reset initialResultsLength here — that causes the table to
     * flip to the onboarding empty state (hiding toolbar/search) when a refresh returns
     * zero results. Use refreshAsInitialLoad() if you need a full state reset.
     */
    refresh() {
        this.initialEmptyState = false;

        this.refresh$.next();
    }

    /**
     * Clears the search input and resets search state.
     * Emits to valueChanges to cancel any pending debounced search.
     * Optionally triggers a refresh after clearing.
     */
    clearSearch(triggerRefresh = false): void {
        this.searchControl.setValue('');
        this.searchState$.next('');

        if (this.mode === TableModes.DATA_ARRAY) {
            this.dataAsDataSource.filter = '';
        }

        if (triggerRefresh) {
            this.refresh();
        }
    }

    /**
     * Full state reset — clears data, shows skeleton, and resets initial empty state tracking.
     * Use this when switching contexts (e.g., tab changes) where you want the table to
     * behave as if it's loading for the first time. If the reload returns zero results,
     * the onboarding empty state will show (toolbar/search hidden).
     *
     * For simple data refreshes (filters, mutations), use refresh() instead — it preserves
     * the toolbar/search even when zero results are returned.
     */
    refreshAsInitialLoad() {
        // Clear the displayed data and reset state to show skeleton
        this.data = [];
        this.resultsLength = 0;
        this.countedResultsLength = 0;
        this.initialResultsLength = -1;
        this.initialEmptyState = false;
        this.isLoadingResults = true;
        // Set this to true - the skeleton shows when this is true
        this.isLoadingInitialResults = true;

        // Use forceRefresh$ which bypasses the isLoadingInitialResults filter
        // This allows the refresh to trigger while still showing the skeleton
        this.forceRefresh$.next();
    }

    clearCheckboxSelection() {
        if (this.checkboxDirective?.instance) {
            this.checkboxDirective.instance.selectedRows.clear();
            this.checkboxDirective.instance.selectedCount = 0;
            this.selection = { ids: [], count: 0 };
            this.checkboxSelection.emit(this.selection);
        }
    }

    resetColumns() {
        this._selectDefaultColumns();

        // User action — drop the stored override so the table reverts to
        // system defaults (DELETE), not just locally.
        this._columnPrefs.clear(this.type);

        // Notify subscribers that columns have changed
        this.columnChange$.next();
    }

    /**
     * Stage the current selection on every toggle — instant local write, no
     * network. The picker stays open for multi-select; the backend write is
     * deferred to {@link persistColumns} on menu close.
     */
    storeColumns() {
        this._columnPrefs.store(this.type, this.selectedColumns.selected);
        this._setMinWidthForTable();
    }

    /**
     * Commit the staged column selection to the backend once the picker
     * closes, collapsing a multi-toggle editing session into a single PUT.
     */
    persistColumns() {
        if (!this.type) {
            return;
        }

        this._columnPrefs.persist(this.type);
    }

    /**
     * Apply a stored selection of visible column keys, or fall back to system
     * defaults when none exist. Reconciliation is implicit: keys no longer in
     * `columnMap` are dropped (intersection), and newly-added columns simply
     * aren't selected — they remain discoverable via the column picker. Untitled columns (which the picker can't toggle) and mobile columns are always
     * selected: the picker is hidden in mobile mode, so a stored list can never opt into one.
     */
    private _applyStoredColumns(keys: string[] | null): void {
        const desired = keys
            ? this.columnMap.filter((column) => column.isMobile || !column.title || keys.indexOf(column.key) > -1)
            : this._defaultColumnSelection();

        // Skip when the selection already matches — e.g. the watch() echo of a
        // toggle the user just made — to avoid redundant columnChange$ churn on
        // every keystroke. Genuine changes (backend value arriving on a fresh
        // device, a different stored set) still fall through and apply.
        if (this._selectionMatches(desired)) {
            return;
        }

        this.selectedColumns.clear();
        this.selectedColumns.select(...desired);
        this._setMinWidthForTable();

        // Notify subscribers that columns have changed
        this.columnChange$.next();
    }

    /**
     * Select the system-default columns from `columnMap` without touching the
     * stored preference — callers decide whether to also clear the override.
     */
    private _selectDefaultColumns(): void {
        this.selectedColumns.clear();
        this.selectedColumns.select(...this._defaultColumnSelection());
        this._setMinWidthForTable();
    }

    /**
     * The system-default columns: every column in `columnMap`, honoring each
     * column's optional `defaultShow` predicate.
     */
    private _defaultColumnSelection(): ColumnMap[] {
        return this.columnMap.filter((column) => {
            if (column.defaultShow) {
                return !!column.defaultShow();
            }

            return true;
        });
    }

    private _selectionMatches(desired: ColumnMap[]): boolean {
        const current = this.selectedColumns.selected;
        if (current.length !== desired.length) {
            return false;
        }

        const currentKeys = new Set(current.map((column) => column.key));

        return desired.every((column) => currentKeys.has(column.key));
    }

    openColumnMenu() {
        if (this.columnMenuTrigger) {
            this.columnMenuTrigger.openMenu();
        }
    }

    /**
     * Returns the button label with an optional selection count
     */
    actionsMenuLabel(label = 'Actions') {
        if (!this.hasCheckboxSelection || !this.showSelectionCount) {
            return `${label}`;
        }

        return this.selection?.count > 0 ? `${label} (${this.selection.count})` : `${label}`;
    }

    /**
     * Toggle expansion state for a row.
     * If expandMultiple is false, collapses other rows when expanding.
     */
    toggleRowExpansion(row: T): void {
        this.expandedRows.toggle(row);
    }

    /**
     * Check if a row is currently expanded.
     */
    isRowExpanded(row: T): boolean {
        return this.expandedRows.isSelected(row);
    }

    /**
     * Collapse all expanded rows.
     */
    collapseAllRows(): void {
        this.expandedRows.clear();
    }

    /**
     * Whether expandable rows are enabled (template is present).
     */
    get hasExpandableRows(): boolean {
        return !!this.expandedRowTemplate;
    }

    readonly defaultSorting = (data: T, sortHeaderId: string) => {
        return this._filteringService.normalizeMixedDataValueForSorting(this._filteringService.extractPath(sortHeaderId, data));
    };

    /**
     * SD-9501: keeps `summaryRowFn` rows out of sorting, paging and the paginator length, then
     * appends them after every page's rows. Wraps MatTableDataSource's own overridable steps, so
     * filtering still applies to summary rows. Idempotent, since the attach runs on every load.
     */
    private _keepSummaryRowsOutOfPaging(dataSource: MatTableDataSource<T>) {
        const isSummaryRow = (row: T) => !!this.summaryRowFn?.(row);
        const isResultRow = (row: T) => !isSummaryRow(row);

        // Checked apart from the paging patch: an SD-9537 `sortData` input is re-assigned raw on
        // every attach, so the wrapper has to be re-applied whenever it isn't the current one.
        const sortData = dataSource.sortData;
        if (!this._summaryRowSorts.has(sortData)) {
            const summaryLastSort = (data: T[], sort: MatSort) => [
                ...sortData(data.filter(isResultRow), sort),
                ...data.filter(isSummaryRow),
            ];
            this._summaryRowSorts.add(summaryLastSort);
            dataSource.sortData = summaryLastSort;
        }

        if (this._summaryRowSources.has(dataSource)) {
            return;
        }
        this._summaryRowSources.add(dataSource);

        // An empty page stays empty, so a search matching only a summary row (e.g. "total") shows
        // the no-data row instead of a lone total over "0–0 of 0".
        const pageData = dataSource._pageData.bind(dataSource);
        dataSource._pageData = (data: T[]) => {
            const page = pageData(data.filter(isResultRow));

            return page.length ? [...page, ...data.filter(isSummaryRow)] : [];
        };

        // Called with filteredData.length each time filtering runs; page over results only.
        const updatePaginator = dataSource._updatePaginator.bind(dataSource);
        dataSource._updatePaginator = () => updatePaginator(this.countResults(dataSource.filteredData));
    }

    /**
     * Configures client-side data operations for DATA_ARRAY mode only.
     *
     * DATA_SOURCE mode intentionally skips this to force server-side operations.
     * Client-side filtering limited to name/title/email fields by default.
     */
    private _attachMatTableDataSourceHandling(dataSource: MatTableDataSource<T>) {
        if (this.mode !== TableModes.DATA_ARRAY) {
            console.warn('This table expects sorting, pagination, and filtering to be done server-side');
            return;
        }

        // mat-paginator
        dataSource.paginator = this.paginator;

        // SD-9537: a table may take over row ordering entirely. Left alone when not supplied, so
        // MatTableDataSource's own implementation stays in place for every other table.
        //
        // ASSIGNED BEFORE `.sort`, and that ordering is load-bearing. MatTableDataSource's `sort`
        // SETTER calls _updateChangeSubscription(), which rebuilds its internal pipeline and emits
        // SYNCHRONOUSLY — sorting the rows there and then. Anything assigned after that first
        // emission is only picked up the next time something re-emits (a header click, new data),
        // so a `sortData` assigned AFTER `.sort` would leave the FIRST render sorted by the
        // default implementation. That is exactly the bug this move fixes: the table came up unsorted by
        // our comparator and only obeyed it once a header was clicked.
        if (this.sortData) {
            dataSource.sortData = this.sortData;
        }

        // SD-9501: also before `.sort`, for the same reason — it wraps whichever sortData is set.
        if (this.summaryRowFn) {
            this._keepSummaryRowsOutOfPaging(dataSource);
        }

        // mat-sort
        dataSource.sort = this.effectiveSort;

        // fallback to default sorting if not set for specific table
        dataSource.sortingDataAccessor = this.sortingDataAccessor || this.defaultSorting;

        // search & table filters
        // TODO: TableFilters (though this may cause a table refresh to occur first)
        const originalFilterFunction = dataSource.filterPredicate;
        const defaultFilter = (data: T, filterString: string) => {
            // a few "common" use cases currently in use for tables
            if (data) {
                const item = data as any;
                const parts: { [prop: string]: any } = {};

                if (item.name) {
                    parts.name = item.name;
                }

                if (item.title) {
                    parts.title = item.title;
                }

                if (item.email) {
                    parts.email = item.email;
                }

                return originalFilterFunction(parts as any, filterString);
            }

            return false;
        };
        dataSource.filterPredicate = this.filterPredicate || defaultFilter;

        // no loading/loaded animations needed for rows with already loaded clientside
        this.rowsAnimated = false;
    }

    private _setMinWidthForTable() {
        if (this.tableRef) {
            // first column width should be minimum 24rem/384px
            // TODO: fix how with gets dynamically set
            const minWidth = '1200px'; // '384px'; // `${384 + (150 * this.selectedColumns.selected.length - 1)}px`;

            // dynamically set min-width based on known columns vs. arbitrary css
            this._renderer.setStyle(this.tableRef.nativeElement, 'min-width', minWidth);
        }
    }

    /**
     * SD-7319 — One-shot URL hydration on init: when persistence is on AND
     * localStorage has stored filters for this table, MERGE them into the
     * current URL so the existing queryParams → filtersState$ flow handles the
     * restore. The URL is authoritative for any keys it already carries
     * (bookmark / shared link / widget-group default like `?state=pending`
     * wins over the stored value); stored filters only fill in keys the URL
     * doesn't have. Using router navigation (rather than setting filters
     * directly) keeps the URL truthy for bookmark / share / browser-back.
     *
     * Skipped silently when:
     *   - the opt-in toggle is off, or
     *   - [type] is not set (no storage key), or
     *   - no stored filters exist for this table, or
     *   - the current URL already contains every stored key (nothing to add).
     */
    private _hydratePersistedFiltersIntoUrl(): boolean {
        if (!this.persistFilterSort || !this.type) {
            return false;
        }

        const stored = this._filterSortPersistence.readFilters(this.type);
        if (!stored || Object.keys(stored).length === 0) {
            return false;
        }

        const currentParams = this._activatedRoute.snapshot.queryParams || {};
        const missing: TableFilters = {};
        for (const key of Object.keys(stored)) {
            if (!(key in currentParams)) {
                missing[key] = stored[key];
            }
        }

        if (Object.keys(missing).length === 0) {
            return false;
        }

        // Seed filtersState$ SYNCHRONOUSLY with the merged set so anything
        // downstream that reads `this.tableRef.filters` between now and the
        // async navigation below — most importantly the consumer's
        // setupTable() subscribing to connectDataTable$ in ngAfterViewInit,
        // whose startWith({}) fires the first API call against the current
        // filter snapshot — sees the hydrated values, not the empty URL
        // state. Without this, the initial widget-group fetch goes out with
        // empty filters and visibly flips to the merged result a moment
        // later (the race that surfaced on back-nav).
        const mergedFilters: TableFilters = { ...currentParams, ...missing };
        this.filters = mergedFilters;

        // Defer the actual URL navigation to next microtask so it doesn't
        // fight ngOnInit's own subscription set-up. The queryParams
        // subscription below will pick up the merged URL emission once the
        // navigation completes — by then the skipPreHydrateEmit guard has
        // already swallowed the pre-navigate empty-URL emission.
        Promise.resolve().then(() => {
            this._router.navigate([], {
                relativeTo: this._activatedRoute,
                queryParams: missing,
                queryParamsHandling: 'merge',
                replaceUrl: true,
            });
        });

        return true;
    }
}
